import { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Pane, Heading, Text, AnnotationIcon, Button, Textarea, majorScale, SelectMenu, Spinner, toaster, SearchIcon } from 'evergreen-ui';
import '../styles/responsive.css';
import { getGuests, getGuestById, updateGuestComment } from '../services/guestsServices';
import { useRole } from '../contexts/RoleContext';

const GuestPeiComment = ({ guests: initialGuests, setGuests }) => {
    const navigate = useNavigate();
    const { isRestrictedView } = useRole();
    const [guests, setLocalGuests] = useState(initialGuests || []);
    const [isLoadingGuests, setIsLoadingGuests] = useState(false);
    const [selectedGuestId, setSelectedGuestId] = useState(null);
    const [selectedGuestLabel, setSelectedGuestLabel] = useState('Seleziona un ospite');
    const [peiUrl, setPeiUrl] = useState(null);
    const [comment, setComment] = useState('');
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        let mounted = true;

        const shouldFetch = !initialGuests || initialGuests.length === 0;

        if (shouldFetch) {
            setIsLoadingGuests(true);
            (async () => {
                try {
                    const g = await getGuests();
                    if (!mounted) return;
                    setLocalGuests(g || []);
                    if (setGuests && (!initialGuests || initialGuests.length === 0)) {
                        setGuests((prev) => {
                            try {
                                if (Array.isArray(prev) && Array.isArray(g) && prev.length === g.length) {
                                    return prev;
                                }
                            } catch (e) {
                            }
                            return g || [];
                        });
                    }
                } catch (err) {
                    console.error('Errore caricamento ospiti per PEI comment:', err);
                    toaster.danger('Errore nel caricamento degli ospiti. Controlla la console per dettagli.');
                } finally {
                    if (mounted) setIsLoadingGuests(false);
                }
            })();
        } else {
            setLocalGuests(initialGuests);
        }

        return () => { mounted = false; };
    }, [initialGuests, setGuests]);

    const items = useMemo(() => (guests || []).map((g) => ({
        label: `${g.nome} ${g.cognome}`,
        value: String(g.id)
    })), [guests]);

    const handleSelect = async (item) => {
        const id = parseInt(item.value, 10);
        setSelectedGuestId(id);
        setSelectedGuestLabel(item.label);
        try {
            const g = await getGuestById(id);
            setPeiUrl(g.revisione_pei_url || null);
            setComment(g.revisione_pei_comment || '');
        } catch (err) {
            console.error('Errore recupero ospite selezionato:', err);
            toaster.danger('Errore nel caricamento dei dati dell\'ospite');
        }
    };

    const handleSave = async () => {
        if (!selectedGuestId) {
            toaster.warning('Seleziona prima un ospite');
            return;
        }
        setSaving(true);
        try {
            await updateGuestComment(selectedGuestId, comment);
            toaster.success('Commento salvato');
            setLocalGuests((prev) => prev.map((g) => g.id === selectedGuestId ? { ...g, revisione_pei_comment: comment } : g));
            if (setGuests) setGuests((prev) => prev.map((g) => g.id === selectedGuestId ? { ...g, revisione_pei_comment: comment } : g));
        } catch (err) {
            console.error('Errore salvataggio commento PEI:', err);
            toaster.danger('Errore durante il salvataggio del commento');
        }
        setSaving(false);
    };

    return (
        <Pane marginTop={majorScale(4)} background="tint1" borderRadius={4}>
            <Heading size={600} marginBottom={12}>Commento revisione PEI</Heading>

            <Pane display="flex" alignItems="center" gap={12} marginBottom={12} className="guest-select-row">
                <SelectMenu
                    title="Seleziona ospite"
                    options={items}
                    selected={selectedGuestId ? String(selectedGuestId) : null}
                    onSelect={handleSelect}
                >
                    <Button>{selectedGuestLabel}</Button>
                </SelectMenu>
                {!isRestrictedView && (
                    <Button
                        onClick={() => {
                            if (!selectedGuestId) {
                                toaster.warning('Seleziona prima un ospite');
                                return;
                            }
                            navigate(`/ricercaNoteOspite?guest_id=${selectedGuestId}`);
                        }}
                    >
                        Ricerca diario di bordo per ospite <SearchIcon marginLeft={8} />
                    </Button>
                )}
                {isLoadingGuests && <Spinner />}
            </Pane>

            <Pane marginBottom={8}>
                <Text fontWeight={600}>Revisione PEI</Text>
                <Pane marginTop={6}>
                    {peiUrl ? (
                        <Text cursor="pointer" onClick={() => window.open(peiUrl, '_blank', 'noopener,noreferrer')} color="muted">
                            {peiUrl.split('/').pop()}
                        </Text>
                    ) : (
                        <Text>Nessuna revisione PEI disponibile</Text>
                    )}
                </Pane>
            </Pane>

            <Pane marginTop={8}>
                <Text fontWeight={600}>Testo commento</Text>
                <Textarea
                    placeholder="Scrivi qui il commento/revisione..."
                    minHeight={200}
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                />
            </Pane>

            <Pane display="flex" justifyContent="flex-end" marginTop={12}>
                <Button appearance="primary" intent="success" onClick={handleSave} isLoading={saving}>Salva commento <AnnotationIcon marginLeft={8} /></Button>
            </Pane>
        </Pane>
    );
};

export default GuestPeiComment;
